import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Modal } from '../../../components/ui/Modal';
import {
  Settings, Save, RefreshCw, TestTube, AlertTriangle, CheckCircle, XCircle, Plus, Edit, Trash2, Eye, EyeOff, CreditCard, DollarSign, Receipt, Calendar, Users, Shield, Activity
} from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const BillingSettings = () => {
  const [settings, setSettings] = useState(null);
  const [billingConfigs, setBillingConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'subscription',
    amount: 0,
    currency: 'USD',
    billingCycle: 'monthly',
    trialPeriod: 0,
    enabled: true,
    description: ''
  });
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [settingsResponse, configsResponse] = await Promise.all([
          MockDataService.getBillingSettings(),
          MockDataService.getBillingConfigurations()
        ]);

        if (settingsResponse.success) {
          setSettings(settingsResponse.data);
        }
        if (configsResponse.success) {
          setBillingConfigs(configsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch billing settings data.");
        console.error("Error fetching billing settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.updateBillingSettings(settings);
      if (response.success) {
        setSettings(response.data);
        alert('Billing settings saved successfully!');
      } else {
        alert('Failed to save billing settings');
      }
    } catch (err) {
      alert('Failed to save billing settings');
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateConfig = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.createBillingConfiguration(formData);
      if (response.success) {
        setBillingConfigs(prev => [...prev, response.data]);
        setShowCreateModal(false);
        setFormData({
          name: '',
          type: 'subscription',
          amount: 0,
          currency: 'USD',
          billingCycle: 'monthly',
          trialPeriod: 0,
          enabled: true,
          description: ''
        });
        alert('Billing configuration created successfully!');
      } else {
        alert('Failed to create billing configuration');
      }
    } catch (err) {
      alert('Failed to create billing configuration');
      console.error("Error creating configuration:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditConfig = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.updateBillingConfiguration(selectedConfig.id, formData);
      if (response.success) {
        setBillingConfigs(prev => prev.map(config => 
          config.id === selectedConfig.id ? response.data : config
        ));
        setShowEditModal(false);
        setSelectedConfig(null);
        alert('Billing configuration updated successfully!');
      } else {
        alert('Failed to update billing configuration');
      }
    } catch (err) {
      alert('Failed to update billing configuration');
      console.error("Error updating configuration:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (window.confirm('Are you sure you want to delete this billing configuration?')) {
      try {
        const response = await MockDataService.deleteBillingConfiguration(configId);
        if (response.success) {
          setBillingConfigs(prev => prev.filter(config => config.id !== configId));
          alert('Billing configuration deleted successfully!');
        } else {
          alert('Failed to delete billing configuration');
        }
      } catch (err) {
        alert('Failed to delete billing configuration');
        console.error("Error deleting configuration:", err);
      }
    }
  };

  const handleTestConfig = async (config) => {
    try {
      setTesting(true);
      setSelectedConfig(config);
      setShowTestModal(true);
      
      const response = await MockDataService.testBillingConfiguration(config.id);
      if (response.success) {
        setTestResults(response.data);
      } else {
        setTestResults({ success: false, message: response.message });
      }
    } catch (err) {
      setTestResults({ success: false, message: 'Test failed' });
      console.error("Error testing configuration:", err);
    } finally {
      setTesting(false);
    }
  };

  const handleToggleConfig = async (configId) => {
    try {
      const config = billingConfigs.find(c => c.id === configId);
      const response = await MockDataService.updateBillingConfiguration(configId, {
        ...config,
        enabled: !config.enabled
      });
      
      if (response.success) {
        setBillingConfigs(prev => prev.map(config => 
          config.id === configId ? response.data : config
        ));
      }
    } catch (err) {
      alert('Failed to toggle configuration status');
      console.error("Error toggling configuration:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'warning': return 'outline';
      default: return 'outline';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'subscription': return <CreditCard className="h-4 w-4" />;
      case 'one-time': return <DollarSign className="h-4 w-4" />;
      case 'invoice': return <Receipt className="h-4 w-4" />;
      case 'trial': return <Calendar className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  if (loading) return <div className="p-6 text-center">Loading Billing Settings...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">Configure billing and payment settings.</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Configuration
          </Button>
          <Button onClick={handleSaveSettings} disabled={saving}>
            <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Global Settings */}
      {settings && (
        <Card>
          <CardHeader>
            <CardTitle>Global Billing Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Default Currency</label>
                <select
                  value={settings.defaultCurrency}
                  onChange={(e) => setSettings({...settings, defaultCurrency: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tax Rate (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.taxRate}
                  onChange={(e) => setSettings({...settings, taxRate: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Grace Period (days)</label>
                <Input
                  type="number"
                  value={settings.gracePeriod}
                  onChange={(e) => setSettings({...settings, gracePeriod: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Auto-renewal</label>
                <select
                  value={settings.autoRenewal ? 'true' : 'false'}
                  onChange={(e) => setSettings({...settings, autoRenewal: e.target.value === 'true'})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Payment Gateway</label>
              <select
                value={settings.paymentGateway}
                onChange={(e) => setSettings({...settings, paymentGateway: e.target.value})}
                className="w-full p-2 border rounded-md"
              >
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="square">Square</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Webhook URL</label>
              <Input
                type="url"
                value={settings.webhookUrl}
                onChange={(e) => setSettings({...settings, webhookUrl: e.target.value})}
                placeholder="https://your-domain.com/webhooks/billing"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Configurations */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Configurations ({billingConfigs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {billingConfigs.map((config) => (
              <div key={config.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getTypeIcon(config.type)}
                      <h3 className="text-lg font-semibold">{config.name}</h3>
                      <Badge variant={getStatusColor(config.status)}>
                        {config.status}
                      </Badge>
                      <Badge variant={config.enabled ? 'success' : 'secondary'}>
                        {config.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{config.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1" />
                        ${config.amount} {config.currency}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {config.billingCycle}
                      </span>
                      <span className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {config.subscriberCount} subscribers
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConfig(config)}
                      disabled={testing}
                    >
                      <TestTube className="h-4 w-4 mr-1" />
                      Test
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedConfig(config);
                        setFormData(config);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleConfig(config.id)}
                    >
                      {config.enabled ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {config.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteConfig(config.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Configuration Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div className="p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-4">Create New Billing Configuration</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Configuration name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="subscription">Subscription</option>
                  <option value="one-time">One-time Payment</option>
                  <option value="invoice">Invoice</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Billing Cycle</label>
                <select
                  value={formData.billingCycle}
                  onChange={(e) => setFormData({...formData, billingCycle: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one-time">One-time</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Trial Period (days)</label>
                <Input
                  type="number"
                  value={formData.trialPeriod}
                  onChange={(e) => setFormData({...formData, trialPeriod: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Configuration description"
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              />
              <label htmlFor="enabled" className="text-sm font-medium">Enable configuration</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateConfig} disabled={saving}>
                <Plus className="h-4 w-4 mr-2" />
                {saving ? 'Creating...' : 'Create Configuration'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Configuration Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
        <div className="p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-4">Edit Billing Configuration</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="subscription">Subscription</option>
                  <option value="one-time">One-time Payment</option>
                  <option value="invoice">Invoice</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Billing Cycle</label>
                <select
                  value={formData.billingCycle}
                  onChange={(e) => setFormData({...formData, billingCycle: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one-time">One-time</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Trial Period (days)</label>
                <Input
                  type="number"
                  value={formData.trialPeriod}
                  onChange={(e) => setFormData({...formData, trialPeriod: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="editEnabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              />
              <label htmlFor="editEnabled" className="text-sm font-medium">Enable configuration</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditConfig} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Test Results Modal */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)}>
        <div className="p-6 max-w-lg">
          <h2 className="text-xl font-bold mb-4">Configuration Test Results</h2>
          {testResults && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${testResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center space-x-2">
                  {testResults.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-medium ${testResults.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResults.success ? 'Test Passed' : 'Test Failed'}
                  </span>
                </div>
                <p className={`mt-2 ${testResults.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResults.message}
                </p>
                {testResults.details && (
                  <div className="mt-3">
                    <h4 className="font-medium text-sm">Details:</h4>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto">
                      {JSON.stringify(testResults.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowTestModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default BillingSettings;
